import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { fetchItemsInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import {
  IN_PROGRESS_TASKS_QUERY_KEY,
  OPEN_TASKS_QUERY_KEY,
  OVERVIEW_TASKS_QUERY_KEY,
} from "@coworker/apprestructured/src/TasksOverview/constants/queryKeys";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import taskTypeOptions from "@coworker/enums/taskType";
import { Trans, useTranslation } from "@coworker/locales";
import { useMutationState, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useOnline } from "../../../core/hooks/useOnline";
import trackerHelper from "../../../helpers/tracker";
import { useRejectPQReport } from "../../../hooks/TaskActions/useCloseTask";
import {
  useAcceptPQReport,
  useCompleteTask,
} from "../../../hooks/TaskActions/useCompleteTask";
import { usePickTask } from "../../../hooks/TaskActions/usePickTask";
import { usePickUpTask } from "../../../hooks/TaskActions/usePickUpTask";
import { useInputPopup } from "../../InputPopup";
import { Reasons } from "../../RoomSettings/constants";
import { useDeleteArticlesByIds } from "../../RoomSettings/hooks/useDeleteArticlesByIds";
import { useUpdateArticle } from "../../RoomSettings/hooks/useUpdateArticle";
import { mapArticleInfoToArticle } from "../../RoomSettings/types/mappers";

function PickUpTaskComponent({ task, taskId }) {
  const { isOnline } = useOnline();
  const { showToast } = useToastNotification();
  const queryClient = useQueryClient();

  const { call: pickUpTask, updateTaskMutation } = usePickUpTask(
    task,
    taskId,
    {}
  );

  return (
    <FixaButton
      onClick={async () => {
        try {
          await pickUpTask();
        } catch (e) {
          if (e.message.includes("picked up")) {
            showToast(
              <Trans>taskAlreadyPickedUpString</Trans>,
              undefined,
              undefined,
              5
            );
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: [
                  OVERVIEW_TASKS_QUERY_KEY,
                  IN_PROGRESS_TASKS_QUERY_KEY,
                ],
              }),
              queryClient.invalidateQueries({
                queryKey: [OVERVIEW_TASKS_QUERY_KEY, OPEN_TASKS_QUERY_KEY],
              }),
            ]);
          }
        }
      }}
      type={"primary"}
      text={
        isOnline ? <Trans>pickUpString</Trans> : <Trans>goOnline2String</Trans>
      }
      size={"medium"}
      disabled={!isOnline || updateTaskMutation.status === "pending"}
      data-testid="pickupButton"
      fluid
      loading={updateTaskMutation.status === "pending"}
    />
  );
}

function CompleteTaskComponent({ task, taskId }) {
  const navigate = useFixaNavigate();
  const { isOnline } = useOnline();
  const { t } = useTranslation();
  const { toggleModal } = useContext(ModalContext);
  const isMFAQTask = task.task_type === taskTypeOptions.MFAQ_FOLLOW_UP;
  const isRoomSettingsTask = task.task_type === taskTypeOptions.ROOM_SETTINGS;
  const isReasonRemoveArticle = task.reason === Reasons.REMOVE_PRODUCT;
  const isReasonReplaceArticle = task.reason === Reasons.REPLACE_PRODUCT;

  const deleteArticlesMutation = useDeleteArticlesByIds(task.media_id);
  const updateArticle = useUpdateArticle(task.article_id);

  const { call: completeTask, updateTaskMutation } = useCompleteTask(
    task,
    taskId,
    {
      undo: true,
      afterUndo: () =>
        toggleModal({
          type: ModalType.TaskDetail,
          payload: { taskId: taskId, taskType: task?.task_type },
        }),
    }
  );

  const deleteArticle = async () => {
    try {
      await deleteArticlesMutation.mutateAsync([task.article_id]);
    } catch {
      alert(t("searchErrorString"));
    }
    navigate(`/mediamaintenance/tabs/${task.media_id}`);
  };

  const replaceArticle = async () => {
    const articlesInfo = await fetchItemsInfo(task.new_product_article_id);

    if (articlesInfo.length > 0) {
      try {
        await updateArticle.mutateAsync({
          ...mapArticleInfoToArticle(articlesInfo[0]),
        });
      } catch (error) {
        console.log("Error while updating article: ", error);
      }
    }
  };

  const isTaskBeingUpdated = updateTaskMutation.status === "pending";
  return (
    <FixaButton
      type={"primary"}
      fluid
      size={"medium"}
      text={
        isOnline ? (
          <Trans>complete2String</Trans>
        ) : (
          <Trans>goOnline3String</Trans>
        )
      }
      loading={isTaskBeingUpdated}
      disabled={
        !isOnline ||
        (isMFAQTask && !task?.finished_summary) ||
        !isValidTestbuyTask(task) ||
        !isValidRoomSettingsTask(task) ||
        isTaskBeingUpdated
      }
      data-testid="confirmButton2"
      onClick={async () => {
        await completeTask();
        trackerHelper.trackTaskCompletion("complete", false);

        if (isRoomSettingsTask && isReasonRemoveArticle) {
          deleteArticle();
        }

        if (isRoomSettingsTask && isReasonReplaceArticle) {
          replaceArticle();
        }
      }}
    />
  );
}

function isValidTestbuyTask(task) {
  if (task.task_type !== taskTypeOptions.TESTBUY_FOLLOW_UP) return true;
  return task.isDone;
}

function isValidRoomSettingsTask(task) {
  const proofOfCompletion = task.isDone ? task.isDone : true;

  if (task.task_type !== taskTypeOptions.ROOM_SETTINGS) return true;
  if (!proofOfCompletion) return false;
  return true;
}

function AcceptPQReportComponent({ task, taskId }) {
  const { call: acceptPQRTask, updateTaskMutation } = useAcceptPQReport(
    task,
    taskId
  );

  const mutationStatus = useMutationState({
    // this mutation key needs to match the mutation key of the given mutation (see above)
    filters: { mutationKey: ["updateTask", taskId] },
    select: (mutation) => mutation.state.status,
  });

  const { isOnline } = useOnline();
  const { getInput } = useInputPopup();

  return (
    <FixaButton
      onClick={async () => {
        const text = await getInput("completeWithText", {
          titleTranslationKey: "acceptIssueString",
          actionTranslationKey: "acceptIssueString",
          placeholderTranslationKey: "feedbackToSenderString",
        });
        if (text) {
          await acceptPQRTask(text);
        }
      }}
      type={"primary"}
      fluid
      loading={updateTaskMutation.status === "pending"}
      text={
        isOnline ? (
          <Trans>acceptActionString</Trans>
        ) : (
          <Trans>goOnline3String</Trans>
        )
      }
      disabled={
        !isOnline ||
        updateTaskMutation.status === "pending" ||
        mutationStatus[0] === "pending"
      }
      data-testid="closeButton2"
    />
  );
}

function RejectPQReportComponent({ task, taskId }) {
  const { call: rejectPQRtask, updateTaskMutation } = useRejectPQReport(
    task,
    taskId
  );
  const mutationStatus = useMutationState({
    // this mutation key needs to match the mutation key of the given mutation (see above)
    filters: { mutationKey: ["updateTask", taskId] },
    select: (mutation) => mutation.state.status,
  });

  const { isOnline } = useOnline();
  const { getInput } = useInputPopup();
  return (
    <FixaButton
      onClick={() =>
        getInput("completeWithText", {
          titleTranslationKey: "closeIssueString",
          actionTranslationKey: "closeIssueString",
          placeholderTranslationKey: "feedbackToSenderString",
        }).then((text) => {
          if (text) rejectPQRtask(text);
        })
      }
      type={"secondary"}
      fluid
      text={
        isOnline ? <Trans>declineString</Trans> : <Trans>goOnline3String</Trans>
      }
      disabled={
        !isOnline ||
        updateTaskMutation.status === "pending" ||
        mutationStatus[0] === "pending"
      }
      loading={updateTaskMutation.status === "pending"}
      data-testid="closeButton2"
    />
  );
}

function CompletePickingComponent({ task, taskId }) {
  const { closePopup } = useInputPopup();
  const { isOnline } = useOnline();
  const { toggleModal } = useContext(ModalContext);

  const { call: pickTask, updateTaskMutation } = usePickTask(task, taskId, {
    undo: true,
    afterUndo: () =>
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId: taskId, taskType: task?.task_type },
      }),
  });

  return (
    <FixaButton
      onClick={() =>
        pickTask().then((handoff) => {
          if (handoff) closePopup();
        })
      }
      type={"primary"}
      fluid
      text={
        isOnline ? (
          <Trans>completePickingButton2String</Trans>
        ) : (
          <Trans>goOnline3String</Trans>
        )
      }
      loading={updateTaskMutation.status === "pending"}
      disabled={!isOnline || updateTaskMutation.status === "pending"}
      data-testid="completePickingButton"
    />
  );
}

const generateWithChanges =
  (title, picking) =>
  ({ taskId }) => {
    const navigate = useFixaNavigate();

    const mutationStatus = useMutationState({
      // this mutation key needs to match the mutation key of the given mutation (see above)
      filters: { mutationKey: ["updateTask", taskId] },
      select: (mutation) => mutation.state.status,
    });

    const { modalState, toggleModal } = useContext(ModalContext);
    return (
      <FixaButton
        type={"secondary"}
        fluid
        text={title}
        size={"medium"}
        disabled={mutationStatus[0] === "pending"}
        onClick={() => {
          if (modalState.isTaskDetailSheetOpen) {
            toggleModal({ type: ModalType.TaskDetail });
          }
          navigate(`/task/${taskId}/changes`, {
            state: {
              picking,
            },
          });
          trackerHelper.trackTaskCompletion("complete_with_changes", true);
        }}
        data-testid="completeWithChangesButton"
      />
    );
  };

export {
  AcceptPQReportComponent,
  CompletePickingComponent,
  CompleteTaskComponent,
  generateWithChanges,
  PickUpTaskComponent,
  RejectPQReportComponent,
};
